import type React from 'react'
import { useCallback } from 'react'
import { FileTypeEnum } from 'utils'

import { MediaControlType } from './Controls/types'
import {
  decreaseVolume,
  increaseVolume,
  muteToggle,
  seekTo,
  showCc,
  toggleFullScreen,
  togglePlay,
} from './store/actions'
import type { Actions, IState } from './store/types'

export const usePlayerHotKeys = (
  dispatch: React.Dispatch<Actions>,
  state: IState,
  duration: number,
  type: FileTypeEnum,
  disableSeeking?: boolean,
) => {
  return useCallback(
    (event: React.KeyboardEvent) => {
      const control: MediaControlType | null =
        (document.activeElement?.getAttribute('data-player-control') as MediaControlType) || null
      let actionFired = false
      switch (event.code) {
        case 'Space':
          if (
            control === MediaControlType.Mute ||
            control === MediaControlType.Fullscreen ||
            control === MediaControlType.PlaybackRate ||
            control === MediaControlType.TimePoints ||
            control === MediaControlType.Subtitles ||
            control === MediaControlType.Captions
          ) {
            break
          }
          actionFired = true
          dispatch(togglePlay())
          if (control === MediaControlType.PlayMask) {
            ;(event.currentTarget as HTMLElement)?.focus()
          }
          break
        case 'ArrowUp':
          if (
            control === MediaControlType.PlaybackRate ||
            control === MediaControlType.TimePoints
          ) {
            break
          }
          actionFired = true
          dispatch(increaseVolume())
          break
        case 'ArrowDown':
          if (
            control === MediaControlType.PlaybackRate ||
            control === MediaControlType.TimePoints
          ) {
            break
          }
          actionFired = true
          dispatch(decreaseVolume())
          break
        case 'ArrowLeft':
          actionFired = true
          if (control === MediaControlType.Volume) {
            break
          }
          dispatch(seekTo(Math.max(0, Number(state.playedSeconds) - 5)))
          break
        case 'ArrowRight': {
          actionFired = true
          if (control === MediaControlType.Volume) {
            break
          }
          const maxForward = disableSeeking
            ? Math.min(Number(duration), state.maxPlayedSeconds ?? 0)
            : Number(duration)
          dispatch(seekTo(Math.min(maxForward, Number(state.playedSeconds) + 5)))
          break
        }
        case 'KeyF':
          // Skip fullscreen for Editor HotKey Shift + F
          if (type == FileTypeEnum.AUDIO || event.shiftKey) {
            break
          }
          actionFired = true
          dispatch(toggleFullScreen())
          break
        case 'KeyM':
          actionFired = true
          dispatch(muteToggle())
          break
        case 'KeyC':
          actionFired = true
          dispatch(showCc())
      }

      if (actionFired) {
        event.preventDefault()
      }
    },
    [dispatch, state, duration, type, disableSeeking],
  )
}
